import type { AverageSales, Buying } from "../types"
import { stockSource } from "./cardText"
import { daysBetween } from "./dates"
import { stockoutDotDay } from "./stockoutDot"

export type StockLineKey = "plan" | "average"

export interface StockLine {
  key: StockLineKey
  label: string
  color: string
  stockoutOn: string | null
  // Chart day of the red dot; null when it falls past the line.
  stockoutDay: number | null
}

export interface StockPoint {
  day: number
  plan?: number
  average?: number
}

export const STOCK_LINE_LABEL: Record<StockLineKey, string> = {
  plan: "Plan",
  average: "Last 90 days",
}

// One line keeps the primary color; next to the plan, the average gets a
// second color.
const COLOR: Record<StockLineKey, string> = {
  plan: "var(--primary)",
  average: "var(--series-2)",
}

// The lines the Projected stock chart draws: the plan's, then the average
// sold or gifted over the last 90 days. The plan's stops where the plan does.
// Next to it, the average's stops at the later of the plan's end and the day
// an order placed today would land (past that it says nothing useful);
// alone, it runs to the window end. A day past a line's end has no value
// for it. Null with neither.
export function stockLines(
  buying: Buying,
  average_sales: AverageSales | null,
  today: string,
): { points: StockPoint[]; lines: StockLine[]; last: number } | null {
  if (stockSource(buying, average_sales) === null) return null

  const series: { key: StockLineKey; values: number[]; stockoutOn: string | null }[] = []
  if (buying.market.planned_units !== null) {
    series.push({ key: "plan", values: buying.market.stock_by_day ?? [], stockoutOn: buying.market.stockout_on })
  }
  if (average_sales) {
    const planEnd = series[0] ? series[0].values.length - 1 : null
    const end = planEnd === null ? Infinity : Math.max(planEnd, daysBetween(today, buying.order_lands_on))
    series.push({ key: "average", values: average_sales.stock_by_day.slice(0, end + 1), stockoutOn: average_sales.stockout_on })
  }

  const last = Math.max(...series.map((s) => s.values.length)) - 1
  const points: StockPoint[] = Array.from({ length: last + 1 }, (_, day) => {
    const point: StockPoint = { day }
    for (const s of series) if (day < s.values.length) point[s.key] = s.values[day]
    return point
  })
  const lines = series.map((s) => ({
    key: s.key,
    label: STOCK_LINE_LABEL[s.key],
    color: series.length > 1 ? COLOR[s.key] : COLOR.plan,
    stockoutOn: s.stockoutOn,
    stockoutDay: stockoutDotDay(today, s.stockoutOn, s.values.length - 1),
  }))
  return { points, lines, last }
}
